<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { IconImage } from '@/shared/ui';
  import TableFillColorActionIcon from '@/shared/assets/icons/TableFillColorActionIcon.svg';
  import TableBrushIcon from '@/shared/assets/icons/TableBrushIcon.svg';
  import TableBorderWidthIcon from '@/shared/assets/icons/TableBorderWidthIcon.svg';
  import TableBorderStyleIcon from '@/shared/assets/icons/TableBorderStyleIcon.svg';
  import TableCellSpacingIcon from '@/shared/assets/icons/TableCellSpacingIcon.svg';
  import { useTableEditorStore } from '../../../../model/table-editor.store';
  import { sanitizeColor, clampNumber } from '../../../../lib/color-sanitizer';
  import { useCellAlignmentControl } from '../../../../model/useCellAlignmentControl';
  import {
    useTableMenuBorders,
  } from '../../../../composables/useTableMenuBorders';
  import {
    useTableMenuActions,
    type MenuVariant,
    type TableMenuActionEmit,
  } from '../../../../composables/useTableMenuActions';
  import ColorPickerButton from './shared/ColorPickerButton.vue';
  import BorderPatternPicker from './shared/BorderPatternPicker.vue';
  import BorderWidthPicker from './shared/BorderWidthPicker.vue';
  import BorderStylePicker from './shared/BorderStylePicker.vue';
  import SpacingSlider from './shared/SpacingSlider.vue';
  import AlignmentPicker from './shared/AlignmentPicker.vue';
  import MenuDropdownButton from './shared/MenuDropdownButton.vue';

  interface Props {
    variant?: MenuVariant;
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'multi',
  });

  const emit = defineEmits<TableMenuActionEmit>();

  const tableEditorStore = useTableEditorStore();
  const isTableVariant = computed(() => props.variant === 'table');

  const firstSelectedCell = computed(() => {
    const cells = tableEditorStore.selectedCellsData;
    return cells.length > 0 ? cells[0] : null;
  });

  const spacingOpen = ref(false);

  const fillColor = computed({
    get: () => firstSelectedCell.value?.properties?.backgroundColor ?? '#ffffff',
    set: (value: string) => {
      tableEditorStore.updateSelectedCellsProperty('backgroundColor', sanitizeColor(value, '#ffffff'));
    },
  });

  const padding = computed({
    get: () => firstSelectedCell.value?.properties?.padding ?? 8,
    set: (value: number) => {
      tableEditorStore.updateSelectedCellsProperty('padding', clampNumber(value, 0, 50));
    },
  });

  const {
    borderPatternOpen,
    borderWidthOpen,
    borderStyleOpen,
    currentPattern,
    currentPatternIcon,
    borderColor,
    borderWidth,
    borderStyle,
    handlePatternChange,
  } = useTableMenuBorders(isTableVariant);

  const { alignmentOpen, alignment, currentAlignmentIcon, handleAlignmentChange } =
    useCellAlignmentControl({
      get: () => firstSelectedCell.value?.properties?.alignment ?? 'left',
      set: (value) => tableEditorStore.updateSelectedCellsProperty('alignment', value),
    });

  const { actionGroups, actionMeta, isActionDisabled, emitAction } = useTableMenuActions(
    () => props.variant,
    emit
  );
</script>

<template>
  <div
    class="table-cell-selection-menu"
    @click.stop
  >
    <div class="menu-style-bar">
      <template v-if="!isTableVariant">
        <ColorPickerButton
          :model-value="fillColor"
          tooltip="Cor de preenchimento"
          :icon-src="TableFillColorActionIcon"
          @update:model-value="fillColor = $event"
        />

        <div class="separator" />

        <MenuDropdownButton
          v-model:open="borderPatternOpen"
          :icon-src="currentPatternIcon"
          label="Seleção de bordas"
        >
          <BorderPatternPicker
            :model-value="currentPattern"
            @update:model-value="handlePatternChange"
          />
        </MenuDropdownButton>
      </template>

      <MenuDropdownButton
        v-model:open="borderWidthOpen"
        :icon-src="TableBorderWidthIcon"
        label="Espessura de bordas"
      >
        <BorderWidthPicker
          :model-value="borderWidth"
          @update:model-value="borderWidth = $event"
        />
      </MenuDropdownButton>

      <MenuDropdownButton
        v-model:open="borderStyleOpen"
        :icon-src="TableBorderStyleIcon"
        label="Estilo de bordas"
      >
        <BorderStylePicker
          :model-value="borderStyle"
          @update:model-value="borderStyle = $event"
        />
      </MenuDropdownButton>

      <ColorPickerButton
        :model-value="borderColor"
        tooltip="Cor das bordas"
        :icon-src="TableBrushIcon"
        @update:model-value="borderColor = $event"
      />

      <template v-if="!isTableVariant">
        <div class="separator" />

        <MenuDropdownButton
          v-model:open="spacingOpen"
          :icon-src="TableCellSpacingIcon"
          label="Espaçamento"
        >
          <SpacingSlider
            :model-value="padding"
            @update:model-value="padding = $event"
          />
        </MenuDropdownButton>
      </template>

      <MenuDropdownButton
        v-model:open="alignmentOpen"
        :icon-src="currentAlignmentIcon"
        label="Alinhamento"
      >
        <AlignmentPicker
          :model-value="alignment"
          @update:model-value="handleAlignmentChange"
        />
      </MenuDropdownButton>

      <template
        v-for="(group, groupIndex) in actionGroups"
        :key="`group-${groupIndex}`"
      >
        <div class="separator" />

        <button
          v-for="action in group"
          :key="action"
          v-tippy="{
            content: actionMeta[action].label,
            placement: 'top',
            theme: 'custom',
            trigger: 'mouseenter',
            hideOnClick: true,
          }"
          type="button"
          class="menu-icon-btn"
          :class="{ danger: actionMeta[action].danger }"
          :aria-label="actionMeta[action].label"
          :disabled="isActionDisabled(action)"
          @click="emitAction(action)"
        >
          <span
            class="action-icon-wrapper"
            :class="{
              'action-icon-wrapper--flip-x': actionMeta[action].iconTransform === 'flip-x',
              'action-icon-wrapper--flip-y': actionMeta[action].iconTransform === 'flip-y',
              'action-icon-wrapper--flip-xy': actionMeta[action].iconTransform === 'flip-xy',
            }"
          >
            <IconImage
              :src="actionMeta[action].icon"
              :alt="actionMeta[action].label"
              class="action-icon"
            />
          </span>
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .table-cell-selection-menu {
    display: flex;
    flex-direction: column;
  }

  .menu-style-bar {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
  }

  .separator {
    width: 1px;
    height: 20px;
    background-color: var(--color-gray-02);
    margin: 0 4px;
  }

  .menu-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    color: var(--color-gray-03);
    transition: background-color 0.15s ease;
  }

  .menu-icon-btn:hover {
    background-color: var(--color-gray-01);
  }

  .menu-icon-btn :deep(svg) {
    width: 20px;
    height: 20px;
    overflow: visible;
  }

  .action-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  .action-icon-wrapper--flip-x {
    transform: scaleX(-1);
    transform-origin: center;
  }

  .action-icon-wrapper--flip-y {
    transform: scaleY(-1);
    transform-origin: center;
  }

  .action-icon-wrapper--flip-xy {
    transform: scale(-1, -1);
    transform-origin: center;
  }

  .menu-icon-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .menu-icon-btn.danger {
    color: var(--color-red-16);
  }

  .menu-icon-btn.danger:hover {
    background-color: #fef2f2;
  }
</style>
